// ============================================================
//  src/context/SiteSettingsContext.js
//  Fetches admin-set site color and site name on app load
//  and applies the primary color as CSS variables globally.
//  NOTE: Page/body background is intentionally left alone —
//  it stays as defined in index.css (--bg: #F2F4FB).
// ============================================================
import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAppSettings } from '../api/client';

const DEFAULTS = {
  siteName:  '2Fast',
  siteColor: '#002CED',
};

const SiteSettingsContext = createContext(DEFAULTS);

// ── Helpers ──────────────────────────────────────────────────
function hexToRgb(hex) {
  const clean = hex.replace('#', '');
  const full  = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;
  const num = parseInt(full, 16);
  return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
}

function darken(hex, amount = 0.15) {
  const clean = hex.replace('#', '');
  const num   = parseInt(clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean, 16);
  const r = Math.max(0, ((num >> 16) & 255) - Math.round(255 * amount));
  const g = Math.max(0, ((num >>  8) & 255) - Math.round(255 * amount));
  const b = Math.max(0, ((num)       & 255) - Math.round(255 * amount));
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
}

// Only applies the primary/brand color — never touches --bg or --page-bg
function applyTheme(siteColor) {
  const root = document.documentElement;

  root.style.setProperty('--primary',       siteColor);
  root.style.setProperty('--primary-dark',  darken(siteColor, 0.15));
  root.style.setProperty('--primary-light', `rgba(${hexToRgb(siteColor)}, 0.09)`);
  root.style.setProperty('--secondary',     darken(siteColor, 0.35));

  root.style.setProperty('--shadow-md', `0 4px 18px rgba(${hexToRgb(siteColor)}, 0.15)`);
  root.style.setProperty('--shadow-lg', `0 8px 32px rgba(${hexToRgb(siteColor)}, 0.20)`);
}

// ── Provider ─────────────────────────────────────────────────
export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);

  useEffect(() => {
    // Apply defaults immediately so page doesn't flash unstyled
    applyTheme(DEFAULTS.siteColor);

    const load = async () => {
      try {
        const { data } = await getAppSettings();
        if (data?.status !== 'success') return;

        const siteColor = data.site_color || DEFAULTS.siteColor;
        const siteName  = data.site_name  || DEFAULTS.siteName;

        applyTheme(siteColor);

        document.title = siteName;

        setSettings({ siteName, siteColor });
      } catch {
        // silently keep defaults — admin color not critical
      }
    };

    load();
  }, []);

  return (
    <SiteSettingsContext.Provider value={settings}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export const useSiteSettings = () => useContext(SiteSettingsContext);